import React from "react";
import { t } from "i18next";
import {
    RefreshIcon,
    StyledIconButton,
    TrashIcon,
} from "@aureskonnect/react-ui";
import ReactSwitch from "react-switch";
import swal from "@sweetalert/with-react";
import classnames from "classnames";

import {
    setDeletedId,
    setIsDeleteModalOpened,
    setPath,
} from "../Website/CoverPage/store";
import { addFiletoStore } from "../fileStore";
import { templateActions } from "@pages/Applications/C&C/C&CSetup/templateStore";

import { CustomMainColorButton } from "@components/Common/CustomMainColorButton";
import { validUploadedTypes } from "../Website/constants";
import { Styles } from "../Website/CoverPage/Styles";
import UpDownArrow from "./UpDownArrow";
import { Table } from "./Table";
import LanguageTable, { AutoResizeTextarea } from "./LanguageTable";
import { templateDefaultValue } from "@constants/template";
import {
    checkDimensions,
    extractDimensions,
    validateFileContent,
} from "../helper";
import { ImageUploadFilledIcon } from "@components/Common/SvgIcons/ImageUploadFilledIcon";

type BannerTableType = {
    section: string;
    path: any;
    deviceType?: any;
    setDeviceType?: Function;
    sizeDesktop?: string;
    sizeMobile?: string;
    redirection?: boolean;
};

export function BannerTable({
    deviceType,
    setDeviceType,
    section,
    path,
    sizeDesktop = "(png,jpg,jpeg ou webp)",
    sizeMobile = "(png,jpg,jpeg ou webp)",
    redirection = false,
}: BannerTableType): JSX.Element {
    const uploadInputRef = React.useRef<HTMLInputElement>(null);
    const isUploadingRef = React.useRef(false);
    let template = templateActions.getNestedObject(path);
    const currentRowIdRef = React.useRef<string | null>(null);
    const isAddingNewRef = React.useRef(false);
    // eslint-disable-next-line
    const data = template ? JSON.parse(JSON.stringify(template)) : [];

    // Local warning state keyed by Rank and deviceType: { [rank]: { [deviceType]: boolean } }
    const [warnings, setWarnings] = React.useState<
        Record<number, Record<string, boolean>>
    >({});

    // Inject local warnings into data for rendering
    const dataWithWarnings = data.map((item: any) => ({
        ...item,
        warning:
            item.Pub?.Url || item.Pub?.Name
                ? warnings[item.Rank]?.[deviceType] || false
                : false,
    }));

    const getFieldsConfig = () => {
        switch (section) {
            case "Bannier":
                return [
                    {
                        key: "Title",
                        label: "Title",
                        placeholder: "Enter the Title",
                    },
                    {
                        key: "Description",
                        label: "Description",
                        placeholder: "Enter the Description",
                    },
                ];
            case "Section2":
                return [
                    {
                        key: "Title",
                        label: "Title",
                        placeholder: "Enter the Title",
                    },
                ];
            default:
                return [];
        }
    };

    function OrderCell({ row, dragHandleProps, className }: any) {
        return (
            <div
                className="d-flex align-items-center position-relative"
                style={{ gap: "20px" }}
            >
                <UpDownArrow
                    dragHandleProps={dragHandleProps}
                    className={className}
                />

                <span
                    style={{
                        font: "normal normal 600 15px/20px Segoe UI",
                        minWidth: "20px",
                        textAlign: "center",
                    }}
                    className="epb_txt_fileOrderAS"
                >
                    {row.original.Rank}
                </span>
            </div>
        );
    }

    // eslint-disable-next-line
    function ActionsCell({ row }: any) {
        let activeScreens = templateActions
            .getNestedObject(path)
            .filter((item: any) => item.active);

        return (
            <div className="d-flex align-items-center justify-content-end position-relative">
                <ReactSwitch
                    disabled={true}
                    uncheckedIcon={false}
                    checkedIcon={false}
                    handleDiameter={26}
                    offColor="#f7b4b8"
                    offHandleColor="#E30613"
                    checked={true}
                    onChange={() => {
                        const localData = [
                            ...templateActions.getNestedObject(path),
                        ];
                        localData[row.id] = {
                            ...localData[row.id],
                            active: !row.original.active,
                        };
                        templateActions.updateTemplate(path, localData);
                    }}
                    onColor="#c2eddd"
                    onHandleColor="#34C38F"
                    width={55}
                    height={20}
                    className="epb_icn_activeDesactiveAS"
                />
                <StyledIconButton
                    icon="TrashIcon"
                    disabled={
                        activeScreens.length === 1 && row.original.active
                            ? true
                            : false
                    }
                    onClick={() => {
                        setPath(path);
                        setDeletedId(row.original.Rank);
                        setIsDeleteModalOpened(section, true);
                    }}
                    aria-label="delete"
                    className="m-0 epb_icn_archiveFileAS"
                >
                    <TrashIcon
                        style={{
                            fill: "red",
                        }}
                        height={20}
                        width={20}
                    />
                </StyledIconButton>
            </div>
        );
    }

    // eslint-disable-next-line
    function UploaderCell({ row }: any) {
        const imageTitle = row.original.Pub?.Name || "No image";
        const imageUrl = row.original.Pub?.Url || "";
        const hasWarning = row.original.warning === true;

        return (
            <div className="d-flex flex-column">
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                    }}
                >
                    <ImageUploadFilledIcon
                        height={40}
                        width={35}
                        className="pointer__clz"
                        onClick={() => {
                            handleUploadImageOnClickEvent(imageUrl);
                        }}
                    />
                    <span
                        style={{
                            fontSize: "14px",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            maxWidth: "200px",
                        }}
                    >
                        {imageTitle}
                    </span>
                </div>
                {hasWarning && (
                    <div
                        style={{
                            fontSize: "80%",
                            color: "#f46a6a",
                        }}
                    >
                        {section === "Section2"
                            ? t(
                                  "The image does not meet the requirements and may affect the display."
                              )
                            : t(
                                  "The image/video does not meet the requirements and may affect the display."
                              )}
                    </div>
                )}
            </div>
        );
    }

    function RedirectionCell({ row }: any) {
        const { Redirection, Rank } = row.original;
        const handleChange = React.useCallback(
            (e: React.ChangeEvent<HTMLTextAreaElement>) => {
                try {
                    const newPath = [
                        ...path,
                        (Rank - 1).toString(),
                        "Redirection",
                    ];
                    templateActions.updateTemplate(newPath, e.target.value);
                } catch (error) {
                    console.error("Error updating redirection:", error);
                }
            },
            // eslint-disable-next-line react-hooks/exhaustive-deps
            []
        );
        return (
            <AutoResizeTextarea value={Redirection} onChange={handleChange} />
        );
    }

    const columns = React.useMemo(
        () => {
            const cols = [
                {
                    Header: t("Rank"),
                    accessor: "Rank",
                    Cell: OrderCell,
                },
                {
                    Header:
                        section === "Section2"
                            ? t("Image")
                            : t("Image or video"),
                    accessor: "Pub",
                    Cell: UploaderCell,
                },
            ];

            if (redirection) {
                cols.push({
                    Header: t("Redirection"),
                    accessor: "redirection",
                    Cell: RedirectionCell,
                });
            }

            cols.push({
                Header: t("Actions"),
                accessor: t("Actions"),
                Cell: ActionsCell,
            });

            return cols;
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [deviceType, warnings]
    );

    async function handleUploadingNewFileRequest(file: File | undefined) {
        if (!file) return;

        if (isUploadingRef.current) return;
        isUploadingRef.current = true;

        const isAddingNew = isAddingNewRef.current;
        const rowIdToUpdate = currentRowIdRef.current;

        isAddingNewRef.current = false;
        currentRowIdRef.current = null;

        if (
            !(section === "Section2"
                ? validUploadedTypes.slice(0, -2)
                : validUploadedTypes
            ).includes(file.type)
        ) {
            isUploadingRef.current = false;
            return swal({
                icon: "error",
                content: <p>{t("File type is not valid")}!</p>,
                buttons: false,
                timer: 2000,
            });
        }

        const isRealMedia = await validateFileContent(file);
        if (!isRealMedia) {
            isUploadingRef.current = false;
            return swal({
                icon: "error",
                content: <p>{t("File content does not match its type")}!</p>,
                buttons: false,
                timer: 2000,
            });
        }

        let showWarning = false;
        const currentSize = deviceType === "Desktop" ? sizeDesktop : sizeMobile;
        const dimensions = extractDimensions(currentSize);

        if (dimensions) {
            const { width, height } = dimensions;
            const isValid = await checkDimensions(file, width, height);
            showWarning = !isValid;
        }

        try {
            if (isAddingNew) {
                const newUrl = URL.createObjectURL(file);
                const newRank = template ? template.length + 1 : 1;
                const newBanner: any = {
                    Pub: {
                        Url: newUrl,
                        Name: file.name,
                        Type: file.type,
                        Extenxion:
                            file.name.split(".").pop()?.toUpperCase() || "",
                    },
                    Rank: newRank,
                    Redirection: "",
                    // No warning saved to store
                };

                const filePath: string = [...path, newBanner.Rank].join(".");
                addFiletoStore(filePath, file);

                const oldData = templateActions.getNestedObject(path) || [];
                templateActions.updateTemplate(path, [...oldData, newBanner]);

                // Save warning locally by rank and deviceType
                setWarnings((prev) => ({
                    ...prev,
                    [newRank]: {
                        ...prev[newRank],
                        [deviceType]: showWarning,
                    },
                }));
            } else if (rowIdToUpdate) {
                if (rowIdToUpdate.startsWith("blob:")) {
                    URL.revokeObjectURL(rowIdToUpdate);
                }
                const newUrl = URL.createObjectURL(file);

                let updatedRank: number | null = null;

                const updatedBanners = templateActions
                    .getNestedObject(path)
                    .map((banner: any) => {
                        if (banner.Pub.Url === rowIdToUpdate) {
                            updatedRank = banner.Rank;
                            const updatedBanner = {
                                ...banner,
                                Pub: {
                                    ...banner.Pub,
                                    Url: newUrl,
                                    Name: file.name,
                                    Type: file.type,
                                    Extenxion:
                                        file.name
                                            .split(".")
                                            .pop()
                                            ?.toUpperCase() || "",
                                },
                                // No warning saved to store
                            };
                            const filePath: string = [
                                ...path,
                                banner.Rank,
                            ].join(".");
                            addFiletoStore(filePath, file);
                            return updatedBanner;
                        }
                        return banner;
                    });

                templateActions.updateTemplate(path, updatedBanners);

                // Save warning locally by rank and deviceType
                if (updatedRank !== null) {
                    setWarnings((prev) => ({
                        ...prev,
                        [updatedRank as number]: {
                            ...prev[updatedRank as number],
                            [deviceType]: showWarning,
                        },
                    }));
                }
            }

            swal({
                icon: "success",
                content: <p>{t("File Uploaded successfully")}!</p>,
                buttons: false,
                timer: 2000,
            });
        } catch (error: any) {
            swal({
                icon: "error",
                content: <p>{t(error.message)}</p>,
                buttons: false,
                timer: 2000,
            });
        } finally {
            isUploadingRef.current = false;
            if (uploadInputRef.current) {
                uploadInputRef.current.value = "";
            }
        }
    }

    function handleUploadImageOnClickEvent(rowId: string) {
        isAddingNewRef.current = false;
        currentRowIdRef.current = rowId;
        uploadInputRef.current?.click();
    }

    function handleUploadedDataOnChangeEvent(
        event: React.ChangeEvent<HTMLInputElement>
    ) {
        const file = event.target.files?.[0];
        if (file) {
            handleUploadingNewFileRequest(file).finally(() => {
                if (uploadInputRef.current) uploadInputRef.current.value = "";
            });
        }
    }

    async function handleResetOnClickEvent(): Promise<void> {
        const currentData = templateActions.getNestedObject(path) || [];
        currentData.forEach((item: any) => {
            if (item.Pub?.Url?.startsWith("blob:")) {
                URL.revokeObjectURL(item.Pub.Url);
            }
        });
        templateActions.updateTemplate(path, []);
        // Clear all local warnings
        setWarnings({});
        if (uploadInputRef.current) {
            uploadInputRef.current.value = "";
        }
    }

    return (
        <div
            className="d-flex flex-column cursor__clz "
            style={{
                gap: "10px",
                flexGrow: 1,
                minHeight: "300px",
            }}
        >
            {!path.includes("Application") ? (
                <span
                    style={{
                        top: "630px",
                        left: "321px",
                        width: "153px",
                        height: "21px",
                        textAlign: "left",
                        font: "normal normal 600 16px/21px Segoe UI",
                        letterSpacing: "0px",
                        color: "#000000",
                        opacity: "1",
                        marginLeft: "3%",
                        marginTop: "1%",
                    }}
                >
                    {section === "Bannier" ? t("Banner image") : t("Images")}
                </span>
            ) : null}

            <div className="d-flex justify-content-between align-items-center">
                {!path.includes("Application") && setDeviceType ? (
                    <div style={{ display: "flex", alignItems: "center" }}>
                        <select
                            value={deviceType}
                            onChange={(e: any) =>
                                setDeviceType(
                                    e.target.value as "Desktop" | "Mobile"
                                )
                            }
                            className="form-select ml-4 py-1 px-2 rounded-sm text-xl"
                            style={{ width: "200px", background: "center" }}
                        >
                            <option value="Desktop">Desktop</option>
                            <option value="Mobile">Mobile</option>
                        </select>
                        <span
                            className="py-2 px-2"
                            style={{ color: "#666", fontSize: "14px" }}
                        >
                            {deviceType === "Desktop"
                                ? sizeDesktop
                                : sizeMobile}
                        </span>
                    </div>
                ) : (
                    <div></div>
                )}
                <div
                    className="d-flex align-items-center"
                    style={{
                        gap: "20px",
                    }}
                >
                    <StyledIconButton
                        title={t("Reset to default settings")}
                        outline={true}
                        variant="danger"
                        className="m-0 p-0 epb_icn_resetModificationsAS"
                        icon="RefreshIcon"
                        onClick={handleResetOnClickEvent}
                    >
                        <RefreshIcon height={20} width={20} />
                    </StyledIconButton>

                    <div className="d-flex">
                        <input
                            accept="video/*,image/*"
                            multiple={false}
                            type="file"
                            id="fileUpload"
                            hidden={true}
                            ref={uploadInputRef}
                            onChange={handleUploadedDataOnChangeEvent}
                            onClick={(event: any) => {
                                event.currentTarget.value = "";
                            }}
                        />
                        <CustomMainColorButton
                            rounded={true}
                            variant="primary"
                            onClick={() => {
                                isAddingNewRef.current = true;
                                uploadInputRef.current?.click();
                            }}
                            className={`${classnames(
                                "w-20 cmn_btn_SaveButton mr-5",
                                {
                                    "not-allowed-icon__clz":
                                        section === "Section1" &&
                                        template !== null &&
                                        template.length === 3,
                                }
                            )}`}
                        >
                            {t("Add")}
                        </CustomMainColorButton>
                    </div>
                </div>
            </div>
            <div className="d-flex flex-column">
                <Styles>
                    <Table
                        columns={columns}
                        data={dataWithWarnings}
                        deviceType={deviceType}
                        section={section}
                        path={path}
                    />
                </Styles>
                {["Bannier", "Section2"].includes(section) && (
                    <LanguageTable
                        path={["SiteWeb", "LaindingPage", section]}
                        fields={getFieldsConfig()}
                        defaultTexts={{
                            Title:
                                section === "Section2"
                                    ? templateDefaultValue.SiteWeb.LaindingPage
                                          .Section2.Title
                                    : templateDefaultValue.SiteWeb.LaindingPage
                                          .Bannier.Title,
                            Description:
                                templateDefaultValue.SiteWeb.LaindingPage
                                    .Bannier.Description,
                        }}
                        sectionTitle={
                            section === "Bannier"
                                ? "Title and Description"
                                : "Title"
                        }
                    />
                )}
            </div>
        </div>
    );
}
